/**
 * Error Fallback Component
 *
 * User-friendly error display for API failures and runtime errors
 * Provides retry and navigation options
 */

import { AlertCircle, RefreshCw, Home, ArrowLeft } from 'lucide-react'

interface ErrorFallbackProps {
  error?: Error
  resetError?: () => void
  title?: string
  message?: string
  showDetails?: boolean
}

export function ErrorFallback({
  error,
  resetError,
  title = 'เกิดข้อผิดพลาด',
  message = 'ขออภัย ไม่สามารถโหลดข้อมูลได้ในขณะนี้ กรุณาลองใหม่อีกครั้ง',
  showDetails = false,
}: ErrorFallbackProps) {
  const handleRetry = () => {
    if (resetError) {
      resetError()
    } else {
      window.location.reload()
    }
  }

  const handleGoBack = () => {
    window.history.back()
  }

  const handleGoHome = () => {
    window.location.href = '/#dashboard'
  }

  return (
    <div className="min-h-[400px] flex items-center justify-center p-6">
      <div className="max-w-lg w-full bg-white rounded-xl shadow-lg border-2 border-orange-200 p-8">
        {/* Icon */}
        <div className="flex justify-center mb-6">
          <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center">
            <AlertCircle className="w-8 h-8 text-korat-orange-600" />
          </div>
        </div>

        {/* Title */}
        <h2 className="text-2xl font-semibold text-korat-navy-700 text-center mb-3">
          {title}
        </h2>

        {/* Message */}
        <p className="text-slate-600 text-center mb-6 leading-relaxed">
          {message}
        </p>

        {/* Error Details (Development Only) */}
        {showDetails && error && import.meta.env.DEV && (
          <details className="mb-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
            <summary className="text-sm font-medium text-slate-700 cursor-pointer hover:text-slate-900">
              รายละเอียดข้อผิดพลาด (สำหรับนักพัฒนา)
            </summary>
            <div className="mt-3 space-y-2">
              <div>
                <span className="text-xs font-semibold text-slate-500">Error Type:</span>
                <p className="text-xs text-red-600 font-mono mt-1">{error.name}</p>
              </div>
              <div>
                <span className="text-xs font-semibold text-slate-500">Message:</span>
                <p className="text-xs text-red-600 font-mono mt-1">{error.message}</p>
              </div>
              {error.stack && (
                <div>
                  <span className="text-xs font-semibold text-slate-500">Stack Trace:</span>
                  <pre className="text-xs text-slate-600 whitespace-pre-wrap mt-1 max-h-32 overflow-auto">
                    {error.stack}
                  </pre>
                </div>
              )}
            </div>
          </details>
        )}

        {/* Action Buttons */}
        <div className="space-y-3">
          {/* Retry Button */}
          <button
            onClick={handleRetry}
            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-korat-navy-700 text-white rounded-lg hover:bg-korat-navy-700 transition-colors font-medium shadow-sm"
          >
            <RefreshCw className="w-5 h-5" />
            ลองใหม่อีกครั้ง
          </button>

          {/* Secondary Actions */}
          <div className="flex gap-3">
            <button
              onClick={handleGoBack}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors font-medium"
            >
              <ArrowLeft className="w-4 h-4" />
              ย้อนกลับ
            </button>
            <button
              onClick={handleGoHome}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors font-medium"
            >
              <Home className="w-4 h-4" />
              หน้าหลัก
            </button>
          </div>
        </div>

        {/* Help Text */}
        <p className="text-xs text-slate-500 text-center mt-6">
          หากปัญหายังคงอยู่ กรุณาติดต่อผู้ดูแลระบบ
        </p>
      </div>
    </div>
  )
}

/**
 * Compact Error Fallback (for inline errors)
 */
interface CompactErrorFallbackProps {
  message?: string
  onRetry?: () => void
}

export function CompactErrorFallback({
  message = 'ไม่สามารถโหลดข้อมูลได้',
  onRetry,
}: CompactErrorFallbackProps) {
  return (
    <div className="flex flex-col items-center justify-center p-6 bg-red-50 border-2 border-red-200 rounded-lg">
      <AlertCircle className="w-8 h-8 text-red-500 mb-3" />
      <p className="text-sm text-slate-700 mb-4">{message}</p>
      {onRetry && (
        <button
          onClick={onRetry}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors text-sm font-medium"
        >
          <RefreshCw className="w-4 h-4" />
          ลองใหม่
        </button>
      )}
    </div>
  )
}
